import React, { useState } from 'react';
import { FaTimes, FaFile, FaDownload, FaEye } from 'react-icons/fa';
import dayjs from 'dayjs';

const API_URL = '/api';

const VendorViewModal = ({ open, onClose, vendor }) => {
  const [loadingFile, setLoadingFile] = useState(null);

  if (!open || !vendor) return null;

  const handleFileDownload = async (fileType) => {
    try {
      setLoadingFile(fileType);
      const response = await fetch(`${API_URL}/vendors/${vendor.vendor_code}/files/${fileType}`);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${vendor.vendor_code}_${fileType}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error(`Error downloading ${fileType}:`, error);
      alert(`Failed to download ${fileType}: ${error.message}`);
    } finally {
      setLoadingFile(null);
    }
  };

  const handleFileView = async (fileType) => {
    try {
      setLoadingFile(fileType);
      const response = await fetch(`${API_URL}/vendors/${vendor.vendor_code}/files/${fileType}`);
      if (!response.ok) {
        throw new Error('Failed to view file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error(`Error viewing ${fileType}:`, error);
      alert(`Failed to view ${fileType}: ${error.message}`);
    } finally {
      setLoadingFile(null);
    }
  };

  const renderFilePreview = (fileType) => {
    if (!vendor[fileType]) return '-';

    const isLoading = loadingFile === fileType;
    const isImage = fileType === 'logo';

    return (
      <div className="flex items-center gap-2">
        {isImage ? (
          <img 
            src={`${API_URL}/vendors/${vendor.vendor_code}/files/${fileType}`} 
            alt="Company Logo" 
            className="w-10 h-10 object-cover rounded"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM2NjY2NjYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIiByeT0iMiI+PC9yZWN0PjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41Ij48L2NpcmNsZT48cG9seWxpbmUgcG9pbnRzPSIyMSAxNSAxNSAxNSA5IDkgMyAxNSI+PC9wb2x5bGluZT48L3N2Zz4=';
            }}
          />
        ) : (
          <FaFile className="text-[#2b6777] text-xl" />
        )}
        <button
          onClick={() => handleFileView(fileType)}
          className="p-2 text-[#2b6777] hover:text-[#52ab98] disabled:opacity-50"
          disabled={isLoading}
          title="View"
        >
          <FaEye />
        </button>
        <button
          onClick={() => handleFileDownload(fileType)}
          className="p-2 text-[#2b6777] hover:text-[#52ab98] disabled:opacity-50"
          disabled={isLoading}
          title="Download"
        >
          <FaDownload />
        </button>
        {isLoading && <span className="text-sm text-gray-500">Loading...</span>}
      </div>
    );
  };

  const sections = [
    {
      title: 'Vendor Information',
      fields: [
        { label: 'Vendor Code', value: vendor.vendor_code },
        { label: 'Vendor Name', value: vendor.vendor_name },
        { label: 'Vendor Type', value: vendor.vendor_type },
        { label: 'Country', value: vendor.country },
        { label: 'GST Number', value: vendor.gst_number || '-' },
        { label: 'PAN Number', value: vendor.pan_number || '-' },
        { label: 'Status', value: vendor.status },
      ]
    },
    {
      title: 'Contact Information',
      fields: [
        { label: 'Contact Person', value: vendor.contact_person_name || '-' },
        { label: 'Email', value: vendor.email },
        { label: 'Phone Number', value: vendor.phone_number || '-' },
      ]
    },
    {
      title: 'Address Information',
      fields: [
        { label: 'Address', value: vendor.registered_address || '-' },
        { label: 'City', value: vendor.city || '-' },
        { label: 'State', value: vendor.state || '-' },
        { label: 'Zip Code', value: vendor.zip_code || '-' },
      ]
    },
    {
      title: 'Bank Information',
      fields: [
        { label: 'Account Number', value: vendor.account_number || '-' },
        { label: 'Account Name', value: vendor.account_name || '-' },
        { label: 'IFSC Code', value: vendor.ifsc_code || '-' },
        { label: 'Bank Name', value: vendor.bank_name || '-' },
        { label: 'Bank Branch', value: vendor.bank_branch || '-' },
      ]
    },
    {
      title: 'Documents',
      fields: [
        {
          label: 'Company Logo',
          value: renderFilePreview('logo')
        },
        {
          label: 'GST Certificate',
          value: renderFilePreview('gst_certificate')
        },
        {
          label: 'Company Registration',
          value: renderFilePreview('company_reg_certificate')
        },
        {
          label: 'NDA Contract',
          value: renderFilePreview('nda_contract_file')
        }
      ]
    },
    {
      title: 'System Information',
      fields: [
        { 
          label: 'Created At', 
          value: vendor.created_at ? dayjs(vendor.created_at).format('YYYY-MM-DD HH:mm') : '-' 
        },
        { 
          label: 'Updated At', 
          value: vendor.updated_at ? dayjs(vendor.updated_at).format('YYYY-MM-DD HH:mm') : '-' 
        },
        { label: 'Created By', value: vendor.created_by || '-' },
        { label: 'Updated By', value: vendor.updated_by || '-' },
      ]
    }
  ];

  return (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-2 sm:px-4">
    <div className="bg-[#E5E1DA] rounded-2xl shadow-2xl w-full max-w-[95%] sm:max-w-4xl p-4 sm:p-8 relative overflow-y-auto max-h-[90vh]">
      {/* Close Icon */}
      <button 
        className="absolute top-3 right-3 sm:top-4 sm:right-4 text-[#2b6777] hover:text-[#52ab98]" 
        onClick={onClose}
      >
        <FaTimes size={20} />
      </button>
      
      {/* Title */}
      <h2 className="text-xl sm:text-2xl font-bold text-[#2b6777] mb-4 sm:mb-6 text-center">
        Vendor Details
      </h2>

      {/* Sections */}
      <div className="space-y-4 sm:space-y-6">
        {sections.map((section, index) => (
          <div key={index} className="bg-white rounded-xl p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-bold text-[#2b6777] mb-3 sm:mb-4">
              {section.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              {section.fields.map((field, fieldIndex) => (
                <div key={fieldIndex} className="flex flex-col">
                  <span className="text-xs sm:text-sm font-medium text-gray-600">
                    {field.label}
                  </span>
                  <span className="text-sm sm:text-base text-gray-900 mt-1">
                    {field.label === 'Status' ? (
                      <span
                        className={`inline-block px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-sm
                          ${field.value === 'Active'
                            ? 'bg-[#52ab98] text-white'
                            : 'bg-gray-300 text-gray-500'}`}
                      >
                        {field.value}
                      </span>
                    ) : (
                      field.value
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-4 sm:mt-6 flex justify-end">
        <button
          className="px-3 sm:px-4 py-2 rounded bg-[#52ab98] hover:bg-[#2b6777] text-white font-semibold shadow transition text-sm sm:text-base"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

};

export default VendorViewModal; 